import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';

import MenuIcon from 'components/Header/MenuIcon';
import { COMMON_COLOR } from 'utils/style';

import type { ChatMessageType } from './ChatMessageInput';
import ChatMessage from './MarkdownRenderer';
import { downloadReport } from './aiChatBotApi';

type ChatMessageWrapperProps = {
  messages: ChatMessageType[];
};

const UserMessage: React.FC<{ text: string }> = ({ text }) => (
  <UserMessageRow>
    <UserMessageBubble>{text}</UserMessageBubble>
  </UserMessageRow>
);

const BotMessage: React.FC<{
  text: string;
  cache_key?: string | null;
  errorType?: 'api' | 'token';
  onRetry?: () => void;
}> = ({ text, cache_key, errorType, onRetry }) => {
  const consecutiveApiErrors = useSelector((state: any) => {
    return state.aiChatBot.consecutiveApiErrors;
  });

  return (
    <BotMessageRow>
      {text === '__LOADING_DOTS__' ? (
        <span data-testid="ai-chatbot-loading">
          <AnimatedSentenceDots />
        </span>
      ) : errorType ? (
        <BotErrorBubble>
          <MenuIcon color={'#FF4D4F'} src={'info'} />
          <span>{text}</span>
          {errorType && consecutiveApiErrors <= 1 && (
            <button onClick={onRetry} aria-label="Retry last message">
              <MenuIcon src={'retry'} />
              Retry
            </button>
          )}
        </BotErrorBubble>
      ) : (
        <BotMessageBubble>
          <ChatMessage content={text} />
          {cache_key && (
            <>
              <p data-testid="ai-chatbot-download-report">Download your latest report!</p>
              <DownloadReportButton onClick={() => downloadReport(cache_key)}>
                <MenuIcon src={'download_blue'} />
                Download
              </DownloadReportButton>
            </>
          )}
        </BotMessageBubble>
      )}
    </BotMessageRow>
  );
};

const sentences = [
  'analysing your query...',
  'thinking...',
  'querying your data...',
  'processing...',
  'looking up the relevant insights...',
  'taking a little longer than usual...',
  'just another minute...'
];

const AnimatedSentenceDots: React.FC = () => {
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);
  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex(prev => (prev + 1) % sentences.length);
        setFade(true);
      }, 400);
    }, 10000);
    return () => clearInterval(interval);
  }, []);
  return (
    <AnimatedDotsWrapper>
      <AiLoadingDots>
        <Dot />
        <Dot />
        <Dot />
      </AiLoadingDots>
      <AnimatedSentence $fade={fade}>{sentences[index]}</AnimatedSentence>
    </AnimatedDotsWrapper>
  );
};

const AnimatedDotsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AnimatedSentence = styled.span<{ $fade: boolean }>`
  font-size: 1em;
  color: ${COMMON_COLOR.GREY};
  opacity: ${({ $fade }) => ($fade ? 1 : 0)};
  transform: translateY(${({ $fade }) => ($fade ? '0' : '10px')});
  transition: opacity 0.4s, transform 0.4s;
`;

// const BotQuestionsSuggestions: React.FC<{
//   suggestions: string[];
//   onSuggestionClick?: (suggestion: { text: string; sender: string }) => void;
// }> = ({ suggestions, onSuggestionClick }) => (
//   <BotSuggestionButtonRow>
//     {suggestions.map((suggestion, idx) => (
//       <SuggestionButton
//         key={idx}
//         type="button"
//         onClick={() => onSuggestionClick && onSuggestionClick({ text: suggestion, sender: 'user' })}
//         aria-label={`Ask: ${suggestion}`}
//       >
//         {suggestion}
//       </SuggestionButton>
//     ))}
//   </BotSuggestionButtonRow>
// );

const ChatMessageWrapper: React.FC<
  ChatMessageWrapperProps & {
    onSuggestionClick?: (suggestion: { text: string; sender: string }) => void;
    onRetry?: () => void;
  }
> = ({ messages, onSuggestionClick, onRetry }) => {
  // const [showSuggestions, setShowSuggestions] = useState<boolean>(true);

  // useEffect(() => {
  //   if (messages.length > 2 && messages[messages.length - 2].sender === 'user') {
  //     setShowSuggestions(false);
  //   }
  // }, [messages]);

  // const handleSuggestionClick = (suggestion: { text: string; sender: string }) => {
  //   setShowSuggestions(false);
  //   if (onSuggestionClick) {
  //     onSuggestionClick(suggestion);
  //   }
  // };

  // Refs for user messages
  const userMessageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const chatWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Find the last user message
    const lastUserIdx = messages
      .map((msg, idx) => (msg.sender === 'user' ? idx : -1))
      .filter(idx => idx !== -1)
      .pop();
    if (
      typeof lastUserIdx === 'number' &&
      userMessageRefs.current[lastUserIdx] &&
      chatWrapperRef.current
    ) {
      const userMsgEl = userMessageRefs.current[lastUserIdx];
      const wrapperEl = chatWrapperRef.current;
      const msgTop = userMsgEl.offsetTop;
      wrapperEl.scrollTo({ top: msgTop, behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <ChatMessagesWrapper ref={chatWrapperRef}>
      {messages.map((msg, idx) =>
        msg.sender === 'user' ? (
          <div ref={el => (userMessageRefs.current[idx] = el)} key={idx}>
            <UserMessage text={msg.text} />
          </div>
        ) : (
          <BotMessage
            key={idx}
            text={msg.text}
            cache_key={msg.cache_key}
            errorType={msg.errorType}
            onRetry={onRetry}
          />
        )
      )}
      {/* {showSuggestions && (
        <BotQuestionsSuggestions
          suggestions={[
            'What is my user distribution?',
            'Show me the sales trends.',
            'How can I improve my dashboard?'
          ]}
          onSuggestionClick={handleSuggestionClick}
        />
      )} */}
    </ChatMessagesWrapper>
  );
};

export default ChatMessageWrapper;

// const SuggestionButton = styled.button`
//   background: ${COMMON_COLOR.WHITE};
//   color: ${COMMON_COLOR.BLUE};
//   border: 1px solid ${COMMON_COLOR.BLUE};
//   border-radius: 16px;
//   padding: 6px 8px;
//   font-family: 'Inter', sans-serif;
//   font-size: 13px;
//   cursor: pointer;
//   transition: background 0.2s;
//   &:hover {
//     background: #2665ee14;
//   }
// `;
const ChatMessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 12px 16px 16px;
  width: 100%;
  max-height: calc(100vh - 268px);
  min-height: calc(100vh - 268px);
  overflow-y: auto;
  overscroll-behavior-y: contain;
  overflow-anchor: none;
`;

const UserMessageRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const BotMessageRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const UserMessageBubble = styled.div`
  max-width: 90%;
  background: #2665ee0d;
  color: ${COMMON_COLOR.GREY};
  border-radius: 8px;
  padding: 10px 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  align-self: flex-end;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

// const BotSuggestionButtonRow = styled.div`
//   display: flex;
//   flex-wrap: wrap;
//   gap: 8px;
// `;

const BotMessageBubble = styled.div`
  max-width: 96%;
  background: ${COMMON_COLOR.WHITE};
  color: ${COMMON_COLOR.GREY};
  border-radius: 8px;
  padding: 10px 0;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  align-self: flex-start;
  word-break: break-word;
  overflow-wrap: anywhere;

  p {
    margin-bottom: 8px;
  }

  .chat-table-wrapper {
    width: 100%;
    max-width: 390px;
    overflow-x: auto;
    margin-bottom: 1rem;
  }

  pre {
    max-width: 390px;
  }

    table {
      width: 100%;
      border-collapse: collapse;
      border-radius: 4px;
      overflow: hidden;
    }
    th,
    td {
      white-space: nowrap;
      font-family: 'Roboto', sans-serif;
      border: 1px solid ${COMMON_COLOR.GREY_2};
    }
    th {
      padding: 14px 24px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      font-style: Medium;
      font-size: 12px;
      leading-trim: NONE;
      line-height: 18px;
      letter-spacing: 0%;
      color: ${COMMON_COLOR.GREY_3};
    }
    td {
      padding: 16px 24px;
      font-family: 'Roboto', sans-serif;
      font-weight: 400;
      font-style: normal;
      font-size: 12px;
      line-height: 18px;
      letter-spacing: 0;
      color: ${COMMON_COLOR.GREY};
    }

    th:first-child {
      border-top-left-radius: 4px;
    }
    th:last-child {
      border-top-right-radius: 4px;
    }
    tr:last-child td:first-child {
      border-bottom-left-radius: 4px;
    }
    tr:last-child td:last-child {
      border-bottom-right-radius: 4px;
    }

    td strong {
      font-family: 'Roboto', sans-serif;
      font-weight: 400;
      font-size: 12px;
      line-height: 18px;
      letter-spacing: 0;
      color: ${COMMON_COLOR.GREY};
    }
  }
`;

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(1) translateY(0); opacity: 0.6; }
  40% { transform: scale(1.3) translateY(-6px); opacity: 1; }
`;

const AiLoadingDots = styled.span`
  display: inline-flex;
  gap: 8px;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${COMMON_COLOR.GREY};
  display: inline-block;
  animation: ${bounce} 1.4s infinite both;
  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

const BotErrorBubble = styled.div`
  max-width: 96%;
  background: #fff1f0;
  color: #f5222d;
  border-radius: 16px 16px 16px 0px;
  padding: 8px 10px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  align-self: flex-start;
  border: 1px solid #ffa39e;
  display: flex;
  align-items: center;
  gap: 6px;
  button {
    display: flex;
    align-items: center;
    gap: 2px;
    background: #ffffffff;
    color: #000000ff;
    border-radius: 6px;
    border: 1px solid ${COMMON_COLOR.QUITE_BORDER};
    padding: 4px 10px;
    font-size: 14px;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    cursor: pointer;
    margin-left: 8px;
    transition: background 0.2s;
    &:hover {
      background: ${COMMON_COLOR.GREY_2};
    }
  }
`;

const DownloadReportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2665ee;
  border-radius: 4px;
  border: 1px solid #2665ee;
  padding: 6px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
  background: #ffffff;
  font-family: Roboto;
  font-weight: 500;
  font-size: 14px;
  line-height: 22px;
  text-align: center;
  vertical-align: middle;

  &:hover {
    background: #bae7ff;
  }
`;
